import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import PaymentForm from '../components/PaymentForm';
import PaymentDetailsHistory from '../components/PaymentDetailsHistory';
import { usePaymentAlerts } from '../hooks/usePaymentAlerts';
import './Payments.css';

const Payments = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [filterEnrollment, setFilterEnrollment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [enrollmentSearch, setEnrollmentSearch] = useState('');
  const [debouncedEnrollmentSearch, setDebouncedEnrollmentSearch] = useState('');
  const [showEnrollmentDropdown, setShowEnrollmentDropdown] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEnrollmentSearch(enrollmentSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [enrollmentSearch]);
  const enrollmentSearchRef = React.useRef(null);
  
  const currentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const iso = (value) => {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    return { from: iso(start), to: iso(end) };
  };
  const initialMonth = currentMonthRange();
  // El mes en curso se arma por vencimiento: un pago adelantado del 30/09
  // sigue perteneciendo a la cuota de octubre si vence en octubre.
  const [filterDateFrom, setFilterDateFrom] = useState(initialMonth.from);
  const [filterDateTo, setFilterDateTo] = useState(initialMonth.to);
  const [dateFromInput, setDateFromInput] = useState(initialMonth.from);
  const [dateToInput, setDateToInput] = useState(initialMonth.to);
  const [dateField, setDateField] = useState('due');
  const [showUrgentFilter, setShowUrgentFilter] = useState(false);
  const [detailsPayment, setDetailsPayment] = useState(null);

  // Obtener alertas de pagos
  const { overdue, dueSoon, totalAlerts } = usePaymentAlerts();

  // Obtener matrículas para el filtro
  const { data: enrollments } = useQuery('enrollments', async () => {
    const response = await api.get('/enrollments', { params: { status: 'active' } });
    return response.data;
  });

  // Obtener estudiantes para mostrar información
  const { data: students } = useQuery('students', async () => {
    const response = await api.get('/students');
    return response.data;
  });

  const { data: paymentStudentMatches = [] } = useQuery(
    ['students-payment-filter', debouncedEnrollmentSearch],
    async () => {
      const response = await api.get('/students', {
        params: { search: debouncedEnrollmentSearch, limit: 15, is_active: true },
      });
      return response.data;
    },
    { enabled: showEnrollmentDropdown && debouncedEnrollmentSearch.length > 0 }
  );

  const { data: paymentEnrollmentMatches = [] } = useQuery(
    ['enrollments-payment-filter', paymentStudentMatches.map((student) => student.id).join(',')],
    async () => {
      const lists = await Promise.all(
        paymentStudentMatches.map((student) =>
          api.get('/enrollments', { params: { student_id: student.id, limit: 20 } }).then((response) =>
            (response.data || []).map((enrollment) => ({
              ...enrollment,
              student: enrollment.student || student,
            }))
          )
        )
      );
      return lists.flat();
    },
    { enabled: paymentStudentMatches.length > 0 }
  );

  // Obtener pagos
  const { data: payments, isLoading, isFetching, error } = useQuery(
    ['payments', filterEnrollment, filterStatus, filterDateFrom, filterDateTo, dateField, showUrgentFilter],
    async () => {
      const params = { limit: 1000 };
      if (filterEnrollment) params.enrollment_id = filterEnrollment;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterDateFrom || filterDateTo) params.date_field = dateField;
      
      // Si el filtro urgente está activo, obtener pagos vencidos y próximos a vencer
      // PERO respetando las fechas seleccionadas
      if (showUrgentFilter) {
        const urgentParams = { ...params, limit: 1000 };
        const [overdueRes, dueSoonRes] = await Promise.all([
          api.get('/payments/overdue', { params: urgentParams }),
          api.get('/payments/due-soon', { params: { ...urgentParams, days_ahead: 3 } })
        ]);
        const urgentPayments = [...(overdueRes.data || []), ...(dueSoonRes.data || [])];
        // Eliminar duplicados por ID
        const uniquePayments = urgentPayments.filter((payment, index, self) =>
          index === self.findIndex(p => p.id === payment.id)
        );
        return uniquePayments;
      }
      
      // Si el filtro es "overdue" (Vencido), usar el endpoint especializado
      // PERO respetando las fechas y matrícula seleccionadas
      if (filterStatus === 'overdue') {
        const response = await api.get('/payments/overdue', { params: { ...params, limit: 1000 } });
        return response.data;
      }
      
      // Para otros estados, usar el endpoint normal con filtro de status
      if (filterStatus) params.status = filterStatus;
      const response = await api.get('/payments', { params });
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (err) => {
        console.error('Error al cargar pagos:', err);
      }
    }
  );

  // Función auxiliar para verificar si un pago está vencido
  // Un pago está vencido solo si la fecha de vencimiento es ANTERIOR a hoy (no igual)
  const isOverdue = React.useCallback((dueDate, status) => {
    if (status === 'paid' || status === 'cancelled') return false;
    
    // Usar solo la fecha sin hora para evitar problemas de zona horaria
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    
    // Parsear fecha de vencimiento (puede venir como string "YYYY-MM-DD" o Date)
    let dueDateStr;
    if (typeof dueDate === 'string') {
      dueDateStr = dueDate.split('T')[0]; // Extraer solo la fecha si viene con hora
    } else {
      dueDateStr = new Date(dueDate).toISOString().split('T')[0];
    }
    
    // Comparar strings de fecha directamente (más confiable)
    const todayDate = new Date(todayStr + 'T00:00:00');
    const dueDateParsed = new Date(dueDateStr + 'T00:00:00');
    const diffTime = dueDateParsed - todayDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Usar floor
    
    // Solo vencido si es estrictamente menor (diffDays < 0)
    return diffDays < 0;
  }, []);

  // Obtener pagos vencidos y filtrar para asegurar que solo sean realmente vencidos
  const { data: overduePayments } = useQuery(
    'overdue-payments',
    async () => {
      const response = await api.get('/payments/overdue');
      const allOverdue = response.data || [];
      // Filtrar en el frontend para asegurar que solo sean realmente vencidos (fecha anterior a hoy)
      return allOverdue.filter(p => {
        if (p.status === 'paid' || p.status === 'cancelled') return false;
        return isOverdue(p.due_date, p.status);
      });
    },
    {
      onError: (err) => {
        console.error('Error al cargar pagos vencidos:', err);
      },
      staleTime: 0, // Siempre considerar datos obsoletos para forzar refetch
      cacheTime: 0, // No cachear para evitar datos desactualizados
    }
  );

  // Calcular estadísticas
  const stats = React.useMemo(() => {
    if (!payments) return null;
    
    const total = payments.length;
    const pending = payments.filter(p => p.status === 'pending').length;
    const paid = payments.filter(p => p.status === 'paid').length;
    const partial = payments.filter(p => p.status === 'partial').length;
    // Vencido: solo si la fecha es ANTERIOR a hoy (no igual)
    // No contar pagos que vencen hoy como vencidos
    const overdue = payments.filter(p => {
      if (p.status === 'paid' || p.status === 'cancelled') return false;
      // Solo contar como vencido si realmente está vencido (fecha anterior a hoy)
      return isOverdue(p.due_date, p.status);
    }).length;
    
    // Total Recaudado: suma todos los paid_amount (incluye pagos completos y parciales)
    const paidAmount = payments.reduce((sum, p) => sum + parseFloat(p.paid_amount || 0), 0);
    
    return {
      total,
      pending,
      paid,
      partial,
      overdue,
      paidAmount
    };
  }, [payments, isOverdue]);

  const createPaymentMutation = useMutation(
    (data) => api.post('/payments', data),
    {
      onSuccess: () => {
        // Invalidar todas las queries relacionadas con pagos
        queryClient.invalidateQueries('payments');
        queryClient.invalidateQueries('overdue-payments');
        queryClient.invalidateQueries('payment-alerts-overdue');
        queryClient.invalidateQueries('payment-alerts-due-soon');
        toast.success('Pago creado exitosamente!');
        setIsModalOpen(false);
        setEditingPayment(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail.forEach(e => toast.error(`${e.loc[1]}: ${e.msg}`));
        } else {
          toast.error(errorDetail || 'Error al crear pago');
        }
      },
    }
  );

  const updatePaymentMutation = useMutation(
    ({ id, data }) => api.put(`/payments/${id}`, data),
    {
      onSuccess: () => {
        // Invalidar todas las queries relacionadas con pagos para actualizar la UI inmediatamente
        queryClient.invalidateQueries('payments');
        queryClient.invalidateQueries('overdue-payments');
        queryClient.invalidateQueries('payment-alerts-overdue');
        queryClient.invalidateQueries('payment-alerts-due-soon');
        toast.success('Pago actualizado exitosamente!');
        setIsModalOpen(false);
        setEditingPayment(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail.forEach(e => toast.error(`${e.loc[1]}: ${e.msg}`));
        } else {
          toast.error(errorDetail || 'Error al actualizar pago');
        }
      },
    }
  );

  const handleCreateClick = () => {
    setEditingPayment(null);
    setIsModalOpen(true);
  };

  // Cerrar dropdown de matrícula al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (enrollmentSearchRef.current && !enrollmentSearchRef.current.contains(e.target)) {
        setShowEnrollmentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Abrir modal automáticamente si viene con action=create
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      handleCreateClick();
      setSearchParams({});
      return;
    }
    const status = searchParams.get('status');
    if (status) {
      setFilterStatus(status);
      setShowUrgentFilter(false);
      setFilterDateFrom('');
      setFilterDateTo('');
      setDateFromInput('');
      setDateToInput('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleEditClick = (payment) => {
    setEditingPayment(payment);
    setIsModalOpen(true);
  };

  // Aplicar filtros de fecha inmediatamente (mejor UX)
  const applyDateFilter = () => {
    if (dateFromInput && dateToInput && dateFromInput > dateToInput) {
      toast.error('La fecha inicial no puede ser posterior a la fecha final');
      return;
    }
    setFilterDateFrom(dateFromInput);
    setFilterDateTo(dateToInput);
  };

  const handleSubmit = (data) => {
    // Normalizar paid_amount: si está vacío o es null, convertirlo a 0
    if (data.paid_amount === '' || data.paid_amount === null || data.paid_amount === undefined) {
      data.paid_amount = 0;
    } else {
      data.paid_amount = parseFloat(data.paid_amount) || 0;
    }
    
    // Si estamos editando un pago existente, validar y calcular el estado automáticamente
    if (editingPayment && data.paid_amount !== undefined && data.amount !== undefined) {
      const totalAmount = parseFloat(data.amount) || 0;
      const originalPaid = parseFloat(editingPayment.paid_amount) || 0;
      const pendingAmount = totalAmount - originalPaid;
      const paidAmount = parseFloat(data.paid_amount) || 0;
      const additionalAmount = paidAmount - originalPaid;
      
      // Si el monto adicional excede el pendiente, ajustarlo automáticamente al pendiente máximo
      if (additionalAmount > pendingAmount) {
        // Ajustar el monto pagado al máximo permitido (monto total)
        data.paid_amount = totalAmount;
        toast.info(`El monto adicional se ajustó automáticamente al pendiente máximo (${pendingAmount.toFixed(2)})`);
      }
      
      // Asegurar que el monto pagado no exceda el monto total (por si acaso)
      const finalPaidAmount = Math.min(parseFloat(data.paid_amount) || 0, totalAmount);
      data.paid_amount = finalPaidAmount;
      
      // Calcular el estado automáticamente según el monto pagado total
      if (finalPaidAmount >= totalAmount && totalAmount > 0) {
        data.status = 'paid';
      } else if (finalPaidAmount > 0 && finalPaidAmount < totalAmount) {
        data.status = 'partial';
      } else if (finalPaidAmount === 0) {
        // Solo cambiar a pending si no está vencido
        const dueDate = new Date(data.due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate >= today) {
          data.status = 'pending';
        }
        // Si está vencido, mantener 'overdue'
      }
    } else {
      // Para nuevos pagos, si no hay pago adelantado, el estado inicial es 'pending'
      if (data.paid_amount === 0 || data.paid_amount === '') {
        data.status = 'pending';
      } else {
        // Si hay pago adelantado, calcular el estado
        const totalAmount = parseFloat(data.amount) || 0;
        const paidAmount = parseFloat(data.paid_amount) || 0;
        if (paidAmount >= totalAmount && totalAmount > 0) {
          data.status = 'paid';
        } else if (paidAmount > 0 && paidAmount < totalAmount) {
          data.status = 'partial';
        } else {
          data.status = 'pending';
        }
      }
    }
    
    if (editingPayment) {
      updatePaymentMutation.mutate({ id: editingPayment.id, data });
    } else {
      createPaymentMutation.mutate(data);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': { label: 'Pendiente', class: 'badge-warning', icon: '⏳' },
      'paid': { label: 'Pagado', class: 'badge-success', icon: '✅' },
      'partial': { label: 'Parcial', class: 'badge-info', icon: '💰' },
      'overdue': { label: 'Vencido', class: 'badge-danger', icon: '⚠️' },
      'cancelled': { label: 'Cancelado', class: 'badge-secondary', icon: '❌' },
    };
    return statusMap[status] || { label: status, class: 'badge-secondary', icon: '❓' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Si es string YYYY-MM-DD, parsearlo directamente sin crear Date (evita problemas de zona horaria)
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    // Si es otro formato, intentar parsearlo
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    // Usar métodos locales para evitar conversión UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  if (isLoading) {
    return <div className="loading">Cargando pagos...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar pagos</h3>
          <p>{error.response?.data?.detail || error.message || 'Error desconocido'}</p>
          <button 
            type="button"
            className="btn btn-primary" 
            onClick={() => queryClient.invalidateQueries('payments')}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Gestión de Pagos</h2>
        <button type="button" className="btn btn-primary" onClick={handleCreateClick}>
          + Nuevo Pago
        </button>
      </div>

      {/* Banner de Alertas */}
      {totalAlerts > 0 && (
        <div className={`alert-banner ${overdue.length > 0 ? 'alert-banner-danger' : 'alert-banner-warning'}`}>
          <div className="alert-banner-content">
            <span className="alert-banner-icon">
              {overdue.length > 0 ? '⚠️' : '⏰'}
            </span>
            <div className="alert-banner-text">
              <strong>
                {overdue.length > 0 
                  ? `${overdue.length} pago${overdue.length !== 1 ? 's' : ''} vencido${overdue.length !== 1 ? 's' : ''}`
                  : ''}
                {overdue.length > 0 && dueSoon.length > 0 ? ' y ' : ''}
                {dueSoon.length > 0 
                  ? `${dueSoon.length} pago${dueSoon.length !== 1 ? 's' : ''} próximo${dueSoon.length !== 1 ? 's' : ''} a vencer`
                  : ''}
              </strong>
              <span>Requieren atención inmediata</span>
            </div>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setShowUrgentFilter(!showUrgentFilter)}
              style={{ 
                backgroundColor: 'white', 
                color: overdue.length > 0 ? '#e74c3c' : '#f39c12',
                border: 'none'
              }}
            >
              {showUrgentFilter ? 'Ver Todos' : 'Ver Urgentes'}
            </button>
          </div>
        </div>
      )}

      {/* Estadísticas */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-label">Total Pagos</div>
              <div className="stat-value">{stats.total}</div>
            </div>
          </div>
          <div className="stat-card stat-success">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-label">Pagados</div>
              <div className="stat-value">{stats.paid}</div>
            </div>
          </div>
          <div className="stat-card stat-warning">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <div className="stat-label">Pendientes</div>
              <div className="stat-value">{stats.pending}</div>
            </div>
          </div>
          <div className="stat-card stat-danger">
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <div className="stat-label">Vencidos</div>
              <div className="stat-value">{stats.overdue}</div>
            </div>
          </div>
          <div className="stat-card stat-info">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-label">Total Recaudado</div>
              <div className="stat-value">{formatCurrency(stats.paidAmount)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Pagos Vencidos - Sección destacada */}
      {overduePayments && overduePayments.length > 0 && (
        <div className="card card-warning">
          <div className="card-header">
            <h3>⚠️ Pagos Vencidos ({overduePayments.length})</h3>
            <span className="overdue-total">
              Total: {formatCurrency(
                overduePayments.reduce((sum, p) => 
                  sum + (parseFloat(p.amount) - parseFloat(p.paid_amount || 0)), 0
                )
              )}
            </span>
          </div>
          <div className="overdue-list">
            {overduePayments.slice(0, 5).map((payment) => {
              // Usar información del estudiante que viene directamente en el pago (si está disponible)
              const student = payment.student || (() => {
                const enrollment = enrollments?.find(e => e.id === payment.enrollment_id);
                return students?.find(s => s.id === enrollment?.student_id);
              })();
              const remaining = parseFloat(payment.amount) - parseFloat(payment.paid_amount || 0);
              
              return (
                <div key={payment.id} className="overdue-item">
                  <div className="overdue-student">
                    <strong>{student ? `${student.first_name} ${student.last_name}` : `Matrícula #${payment.enrollment_id}`}</strong>
                    <span className="overdue-date">Vence: {formatDate(payment.due_date)}</span>
                  </div>
                  <div className="overdue-amount">
                    <span className="amount-label">Pendiente:</span>
                    <span className="amount-value">{formatCurrency(remaining)}</span>
                  </div>
                  <button 
                    type="button"
                    className="btn btn-primary btn-sm" 
                    onClick={() => handleEditClick(payment)}
                  >
                    Pagar
                  </button>
                </div>
              );
            })}
            {overduePayments.length > 5 && (
              <div className="overdue-more">
                Y {overduePayments.length - 5} pago(s) vencido(s) más...
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="filters-bar">
          <div className="enrollment-search-wrapper" ref={enrollmentSearchRef} style={{ width: '200px', flexShrink: 0 }}>
            <input
              type="text"
              className="enrollment-search-input"
              placeholder="🔍 Buscar alumno..."
              style={{ width: '200px', boxSizing: 'border-box', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', height: '42px' }}
              value={enrollmentSearch}
              onChange={(e) => {
                setEnrollmentSearch(e.target.value);
                setShowEnrollmentDropdown(true);
                if (!e.target.value) {
                  setFilterEnrollment('');
                }
              }}
              onFocus={() => setShowEnrollmentDropdown(true)}
              autoComplete="off"
            />
            {filterEnrollment && (
              <button
                type="button"
                className="enrollment-search-clear"
                onClick={() => {
                  setEnrollmentSearch('');
                  setFilterEnrollment('');
                  setShowEnrollmentDropdown(false);
                }}
                title="Limpiar"
              >×</button>
            )}
            {showEnrollmentDropdown && (
              <div className="enrollment-dropdown">
                <div
                  className="enrollment-dropdown-item"
                  onMouseDown={() => {
                    setFilterEnrollment('');
                    setEnrollmentSearch('');
                    setShowEnrollmentDropdown(false);
                  }}
                >
                  Todos los alumnos
                </div>
                {(debouncedEnrollmentSearch ? paymentEnrollmentMatches : (enrollments || []).slice(0, 15))
                  .map((enrollment) => {
                    const student = enrollment.student || students?.find(s => s.id === enrollment.student_id);
                    const name = student ? `${student.first_name} ${student.last_name}` : `Matrícula #${enrollment.id}`;
                    const doc = student?.document_number;
                    return (
                      <div
                        key={enrollment.id}
                        className={`enrollment-dropdown-item ${filterEnrollment === String(enrollment.id) ? 'selected' : ''}`}
                        onMouseDown={() => {
                          setFilterEnrollment(String(enrollment.id));
                          setEnrollmentSearch(name);
                          setShowEnrollmentDropdown(false);
                        }}
                      >
                        <span>{name}</span>
                        <span className="enrollment-dropdown-id">{doc ? `DNI: ${doc}` : `#${enrollment.id}`}</span>
                      </div>
                    );
                  })
                }
                {debouncedEnrollmentSearch && paymentStudentMatches.length === 0 && (
                  <div className="enrollment-dropdown-empty">No se encontraron alumnos</div>
                )}
                {debouncedEnrollmentSearch && paymentStudentMatches.length > 0 && paymentEnrollmentMatches.length === 0 && (
                  <div className="enrollment-dropdown-empty">El alumno no tiene matrículas</div>
                )}
              </div>
            )}
          </div>

          <select
            value={showUrgentFilter ? 'urgent' : filterStatus}
            onChange={(e) => {
              if (e.target.value === 'urgent') {
                setShowUrgentFilter(true);
                setFilterStatus('');
              } else {
                setShowUrgentFilter(false);
                setFilterStatus(e.target.value);
              }
            }}
            className="filter-select"
          >
            <option value="">Todos los estados</option>
            {totalAlerts > 0 && (
              <option value="urgent">
                ⚠️ Urgentes ({totalAlerts}) - Vencidos + Próximos a vencer
              </option>
            )}
            <option value="pending">Pendiente</option>
            <option value="paid">Pagado</option>
            <option value="partial">Parcial</option>
            <option value="overdue">Vencido</option>
          </select>

          <div className="date-filter-wrapper">
            <div className="filter-group">
              <label>Ver por</label>
              <select
                value={dateField}
                onChange={(e) => setDateField(e.target.value)}
                className="filter-select payment-date-field"
                title="Por vencimiento incluye pagos adelantados de la cuota del mes"
              >
                <option value="due">Vencimiento</option>
                <option value="payment">Fecha de pago</option>
              </select>
            </div>
            <button
              type="button"
              className="payment-date-clear"
              onClick={() => {
                const month = currentMonthRange();
                setDateField('due');
                setDateFromInput(month.from);
                setDateToInput(month.to);
                setFilterDateFrom(month.from);
                setFilterDateTo(month.to);
              }}
            >
              Mes en curso
            </button>
            <div className="filter-group">
              <label>{dateField === 'due' ? 'Vence desde' : 'Pagado desde'}</label>
              <input
                type="date"
                value={dateFromInput}
                max={dateToInput || undefined}
                onChange={(e) => setDateFromInput(e.target.value)}
                className="filter-date"
                style={{ width: '160px', minWidth: '0', boxSizing: 'border-box', padding: '10px', fontSize: '14px', height: '42px' }}
              />
            </div>

            <div className="filter-group">
              <label>{dateField === 'due' ? 'Vence hasta' : 'Pagado hasta'}</label>
              <input
                type="date"
                value={dateToInput}
                min={dateFromInput || undefined}
                onChange={(e) => setDateToInput(e.target.value)}
                className="filter-date"
                style={{ width: '160px', minWidth: '0', boxSizing: 'border-box', padding: '10px', fontSize: '14px', height: '42px' }}
              />
            </div>
            <button
              type="button"
              className="payment-date-apply"
              onClick={applyDateFilter}
            >
              Filtrar fechas
            </button>
            {(dateFromInput || dateToInput || filterDateFrom || filterDateTo) && (
              <button
                type="button"
                className="payment-date-clear"
                onClick={() => {
                  setDateFromInput('');
                  setDateToInput('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                }}
              >
                Limpiar fechas
              </button>
            )}
          </div>
        </div>

        {isFetching && <div className="fetching-indicator">Actualizando...</div>}

        <div className="table-container">
          <table className="table payments-table">
            <thead>
              <tr>
                <th>Matrícula</th>
                <th>Fecha Pago</th>
                <th>Vencimiento</th>
                <th>Monto</th>
                <th className="payments-col-paid">Pagado</th>
                <th>Pendiente</th>
                <th>Estado</th>
                <th>Método</th>
                <th className="payments-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {payments && payments.length > 0 ? (
                payments.map((payment) => {
                  // Usar información del estudiante que viene directamente en el pago (si está disponible)
                  // Si no está disponible, buscar en las listas locales como fallback
                  const student = payment.student || (() => {
                    const enrollment = enrollments?.find(e => e.id === payment.enrollment_id);
                    return students?.find(s => s.id === enrollment?.student_id);
                  })();
                  const enrollment = payment.enrollment || enrollments?.find(e => e.id === payment.enrollment_id);
                  const statusInfo = getStatusLabel(payment.status);
                  const remaining = parseFloat(payment.amount) - parseFloat(payment.paid_amount);
                  
                  // Calcular días hasta vencimiento
                  // Usar solo la fecha sin hora para evitar problemas de zona horaria
                  const today = new Date();
                  const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
                  
                  // Parsear fecha de vencimiento (puede venir como string "YYYY-MM-DD" o Date)
                  let dueDateStr;
                  if (typeof payment.due_date === 'string') {
                    dueDateStr = payment.due_date.split('T')[0]; // Extraer solo la fecha si viene con hora
                  } else {
                    dueDateStr = new Date(payment.due_date).toISOString().split('T')[0];
                  }
                  
                  // Comparar strings de fecha directamente (más confiable)
                  const todayDate = new Date(todayStr + 'T00:00:00');
                  const dueDate = new Date(dueDateStr + 'T00:00:00');
                  const diffTime = dueDate - todayDate;
                  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Usar floor en lugar de ceil
                  
                  // Vencido: fecha de vencimiento es ANTERIOR a hoy (diffDays < 0)
                  const isPaymentOverdue = diffDays < 0 && payment.status !== 'paid' && payment.status !== 'cancelled';
                  
                  // Vence hoy: diffDays === 0
                  const isDueToday = diffDays === 0 && payment.status !== 'paid' && payment.status !== 'cancelled';
                  
                  // Próximo a vencer: vence en los próximos 3 días (1-3)
                  const isDueSoon = !isPaymentOverdue && !isDueToday && diffDays > 0 && diffDays <= 3 && payment.status !== 'paid' && payment.status !== 'cancelled';
                  
                  return (
                    <tr 
                      key={payment.id} 
                      className={
                        isPaymentOverdue ? 'row-overdue' : 
                        isDueToday ? 'row-due-soon' : // Vence hoy se muestra en amarillo
                        isDueSoon ? 'row-due-soon' : ''
                      }
                    >
                      <td>
                        {student ? (
                          <strong>{student.first_name} {student.last_name}</strong>
                        ) : (
                          `Matrícula #${payment.enrollment_id}`
                        )}
                        {enrollment && (
                          <div className="enrollment-info">
                            Matrícula #{enrollment.id}
                          </div>
                        )}
                      </td>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>
                        <span className={
                          isPaymentOverdue ? 'date-overdue' : 
                          isDueToday ? 'date-due-soon' : // Vence hoy también se muestra en naranja
                          isDueSoon ? 'date-due-soon' : ''
                        }>
                          {formatDate(payment.due_date)}
                        </span>
                      </td>
                      <td>
                        <strong>{formatCurrency(payment.amount)}</strong>
                      </td>
                      <td className="payments-col-paid">
                        {parseFloat(payment.paid_amount) > 0 ? (
                          <span className="paid-amount">{formatCurrency(payment.paid_amount)}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {remaining > 0 ? (
                          <span className="remaining-amount">{formatCurrency(remaining)}</span>
                        ) : (
                          <span className="fully-paid">✓ Pagado</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusInfo.class}`}>
                          {statusInfo.icon} {statusInfo.label}
                        </span>
                      </td>
                      <td>
                        {payment.payment_method ? (
                          <span className="payment-method">
                            {payment.payment_method === 'cash' ? '💵 Efectivo' :
                             payment.payment_method === 'card' ? '💳 Tarjeta' :
                             payment.payment_method === 'transfer' ? '🏦 Transferencia' :
                             payment.payment_method === 'yape' ? '📱 Yape' :
                             payment.payment_method === 'plin' ? '📱 Plin' :
                             '📝 Otro'}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="payments-col-actions">
                        <div className="action-buttons">
                          <button 
                            type="button"
                            className="btn btn-secondary btn-sm" 
                            onClick={() => handleEditClick(payment)}
                          >
                            {['pending', 'partial', 'overdue'].includes(payment.status) ? 'Registrar pago' : 'Editar'}
                          </button>
                          {(payment.status === 'partial' || payment.status === 'paid' || parseFloat(payment.paid_amount) > 0) && (
                            <button 
                              type="button"
                              className="btn btn-info btn-sm" 
                              onClick={() => setDetailsPayment(payment)}
                              title="Ver historial de abonos"
                            >
                              Historial
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>
                    {filterEnrollment || filterStatus || filterDateFrom || filterDateTo
                      ? 'No hay pagos que coincidan con los filtros seleccionados'
                      : 'No hay pagos registrados'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={
          editingPayment
            ? (['pending', 'partial', 'overdue'].includes(editingPayment.status) ? 'Registrar pago' : 'Editar Pago')
            : 'Nuevo Pago'
        }
        closeOnOverlayClick={false}
      >
        <PaymentForm
          initialData={editingPayment}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
          isLoading={createPaymentMutation.isLoading || updatePaymentMutation.isLoading}
        />
      </Modal>

      <Modal
        isOpen={!!detailsPayment}
        onClose={() => setDetailsPayment(null)}
        title={`Historial de Abonos — Pago #${detailsPayment?.id || ''}`}
      >
        {detailsPayment && (
          <PaymentDetailsHistory
            paymentId={detailsPayment.id}
            totalAmount={detailsPayment.amount}
            paidAmount={detailsPayment.paid_amount}
          />
        )}
      </Modal>
    </div>
  );
};

export default Payments;
